import { useState } from 'react';
import { useConfig, type Team } from '../store/config';
import { Badge, Button, Card, Checkbox, Dialog, Field, StateBlock, TextInput } from '../ui';
import { useToast } from '../ui/toast';

const MAX_TEAM_SIZE = 12;

export default function TeamsPage() {
  const { config, setConfig, shared, saveError } = useConfig();
  const toast = useToast();

  const [editing, setEditing] = useState<Team | null>(null);
  const [isNew, setIsNew] = useState(false);
  const [confirmDelete, setConfirmDelete] = useState<Team | null>(null);

  const startAdd = () => {
    setIsNew(true);
    setEditing({ id: `team-${Date.now()}`, name: '', playerSteamIds: [] });
  };

  const startEdit = (team: Team) => {
    setIsNew(false);
    setEditing({ ...team, playerSteamIds: [...team.playerSteamIds] });
  };

  const toggleMember = (steamId: string) => {
    setEditing((current) => {
      if (!current) return current;
      const isMember = current.playerSteamIds.includes(steamId);
      if (!isMember && current.playerSteamIds.length >= MAX_TEAM_SIZE) {
        toast.error(`A team holds at most ${MAX_TEAM_SIZE} players`);
        return current;
      }
      return {
        ...current,
        playerSteamIds: isMember
          ? current.playerSteamIds.filter((id) => id !== steamId)
          : [...current.playerSteamIds, steamId],
      };
    });
  };

  const save = async () => {
    if (!editing) return;
    const name = editing.name.trim();
    if (!name) {
      toast.error('A team needs a name');
      return;
    }

    const next = isNew
      ? [...config.teams, { ...editing, name }]
      : config.teams.map((team) => (team.id === editing.id ? { ...editing, name } : team));

    if (!await setConfig({ ...config, teams: next })) return;
    setEditing(null);
  };

  const remove = async (team: Team) => {
    if (!await setConfig({ ...config, teams: config.teams.filter((candidate) => candidate.id !== team.id) })) return;
    setConfirmDelete(null);
  };

  const nameFor = (steamId: string) =>
    config.players.find((player) => player.steamId === steamId)?.name || steamId;

  return (
    <div className="space-y-6">
      <p className="text-sm text-ink-dim">{shared ? 'Shared tournament rosters' : 'Browser rosters'}. Select active starters for each fixture.</p>
      {saveError && <p role="alert" className="text-sm text-warn">{saveError}</p>}
      <Card
        title="Teams"
        description="Used to invite a full side at once when creating a lobby."
        actions={
          <Button size="sm" variant="primary" onClick={startAdd}>
            Add team
          </Button>
        }
      >
        {config.teams.length === 0 ? (
          <StateBlock
            title="No teams yet"
            detail="Group saved players into teams to invite Radiant and Dire in one go."
            action={
              <Button size="sm" variant="primary" onClick={startAdd}>
                Add team
              </Button>
            }
          />
        ) : (
          <div className="grid gap-4 md:grid-cols-2 xl:grid-cols-3">
            {config.teams.map((team) => (
              <div key={team.id} className="rounded-lg border border-line bg-surface-2/50 p-4">
                <div className="flex items-start justify-between gap-2">
                  <h3 className="truncate text-sm font-semibold text-ink">
                    {team.name || 'Unnamed team'}
                  </h3>
                  <Badge tone={team.playerSteamIds.length === MAX_TEAM_SIZE ? 'ok' : 'neutral'}>
                    {team.playerSteamIds.length}/{MAX_TEAM_SIZE}
                  </Badge>
                </div>
                <ul className="mt-3 space-y-1">
                  {team.playerSteamIds.length === 0 ? (
                    <li className="text-xs text-ink-faint">No players assigned</li>
                  ) : (
                    team.playerSteamIds.map((steamId) => (
                      <li key={steamId} className="truncate text-xs text-ink-dim">
                        {nameFor(steamId)}
                      </li>
                    ))
                  )}
                </ul>
                <div className="mt-4 flex gap-2">
                  <Button size="sm" onClick={() => startEdit(team)}>
                    Edit
                  </Button>
                  <Button size="sm" variant="danger" onClick={() => setConfirmDelete(team)}>
                    Delete
                  </Button>
                </div>
              </div>
            ))}
          </div>
        )}
      </Card>

      <Dialog
        open={editing !== null}
        onClose={() => setEditing(null)}
        title={isNew ? 'Add team' : 'Edit team'}
        footer={
          <>
            <Button variant="ghost" onClick={() => setEditing(null)}>
              Cancel
            </Button>
            <Button variant="primary" onClick={save}>
              Save team
            </Button>
          </>
        }
      >
        {editing && (
          <div className="space-y-4">
            <Field label="Team name">
              {({ id }) => (
                <TextInput
                  id={id}
                  value={editing.name}
                  onChange={(event) => setEditing({ ...editing, name: event.target.value })}
                />
              )}
            </Field>

            <div>
              <p className="mb-2 text-xs font-medium tracking-wide text-ink-dim uppercase">
                Members ({editing.playerSteamIds.length}/{MAX_TEAM_SIZE})
              </p>
              {config.players.length === 0 ? (
                <p className="text-xs text-ink-faint">
                  No saved players yet — add them on the Players page first.
                </p>
              ) : (
                <div className="grid max-h-64 gap-1 overflow-y-auto rounded-md border border-line bg-surface-2/50 p-3 sm:grid-cols-2 scrollbar-slim">
                  {config.players.map((player) => (
                    <Checkbox
                      key={player.steamId}
                      label={player.name || player.steamId}
                      checked={editing.playerSteamIds.includes(player.steamId)}
                      onChange={() => toggleMember(player.steamId)}
                    />
                  ))}
                </div>
              )}
            </div>
          </div>
        )}
      </Dialog>

      <Dialog
        open={confirmDelete !== null}
        onClose={() => setConfirmDelete(null)}
        size="sm"
        title="Delete this team?"
        footer={
          <>
            <Button variant="ghost" onClick={() => setConfirmDelete(null)}>
              Cancel
            </Button>
            <Button variant="danger" onClick={() => confirmDelete && remove(confirmDelete)}>
              Delete team
            </Button>
          </>
        }
      >
        <p className="text-sm text-ink-dim">
          {confirmDelete?.name || 'This team'} is removed. The players stay saved.
        </p>
      </Dialog>
    </div>
  );
}
